// ─── PART 4: MARKETS TAB ──────────────────────────────────────────────────────
// Dependencies: part1_core (C, s, fmt, pct, fmtK, clr, DataStatus, AnalystButton, LiveTicker)
// Exports: MarketsTab
//
// Data sources:
//   Alpaca REST  — snapshots (price, change, volume) polled every 30s
//   Alpaca WS    — live bid/ask via LiveTicker component (1s throttle)
//   Alpaca REST  — 30-day daily OHLCV bars for selected symbol chart
//
// onData callbacks pushed to globalSignals:
//   "quotes"  — current snapshot map by symbol

import { useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart,
} from "recharts";
import { RefreshCw, AlertCircle } from "lucide-react";
import {
  C, s, font, fmt, pct, fmtK, clr,
  DataStatus, AnalystButton, LiveTicker,
  ALPACA_REST, backendFetch,
} from "./core";
import { TickerNewsPanel } from "./TickerNewsPanel";

// ─── INDEX MANIFEST ───────────────────────────────────────────────────────────
const INDICES = [
  { symbol: "SPY",  name: "S&P 500 ETF",      sector: "broad" },
  { symbol: "QQQ",  name: "Nasdaq 100 ETF",   sector: "tech" },
  { symbol: "DIA",  name: "Dow Jones ETF",    sector: "broad" },
  { symbol: "IWM",  name: "Russell 2000 ETF", sector: "small" },
  { symbol: "GLD",  name: "Gold ETF",         sector: "commodity" },
  { symbol: "TLT",  name: "20yr Treasury ETF",sector: "rates" },
  { symbol: "USO",  name: "Crude Oil ETF",    sector: "energy" },
  { symbol: "XLF",  name: "Financials ETF",   sector: "financial" },
  { symbol: "XLE",  name: "Energy ETF",       sector: "energy" },
  { symbol: "XLV",  name: "Healthcare ETF",   sector: "health" },
  { symbol: "XLK",  name: "Technology ETF",   sector: "tech" },
  { symbol: "HYG",  name: "HY Bond ETF",      sector: "credit" },
];

// First 8 shown in grid — rest available via search
const GRID_SYMBOLS = INDICES.slice(0, 8);

// ─── CUSTOM TOOLTIP ───────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 3,
      padding: "7px 10px",
      fontSize: 13,
      fontFamily: font,
    }}>
      <div style={{ color: C.textDim, marginBottom: 4 }}>{label}</div>
      {d?.close  != null && <div>Close  <span style={{ color: C.amber }}>${fmt(d.close)}</span></div>}
      {d?.open   != null && <div>Open   <span style={{ color: C.textMid }}>${fmt(d.open)}</span></div>}
      {d?.high   != null && <div>High   <span style={{ color: C.green }}>${fmt(d.high)}</span></div>}
      {d?.low    != null && <div>Low    <span style={{ color: C.red }}>${fmt(d.low)}</span></div>}
      {d?.vol    != null && <div>Volume <span style={{ color: C.textMid }}>{fmtK(d.vol)}</span></div>}
    </div>
  );
}

// ─── QUOTE CARD ───────────────────────────────────────────────────────────────
function QuoteCard({ idx, quote, selected, onClick }) {
  const up = quote?.changePct >= 0;
  const isSelected = selected === idx.symbol;

  return (
    <div
      onClick={onClick}
      style={{
        ...s.panel,
        padding: "9px 11px",
        cursor: "pointer",
        marginBottom: 0,
        borderColor: isSelected ? C.amber : C.border,
        borderLeftWidth: isSelected ? 2 : 1,
        borderLeftColor: isSelected ? C.amber : C.border,
        transition: "border-color 0.1s",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: isSelected ? C.amber : C.text }}>
            {idx.symbol}
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 1 }}>{idx.name}</div>
        </div>
        {quote?.changePct != null && (
          <span style={s.badge(up)}>{pct(quote.changePct)}</span>
        )}
      </div>

      <div style={{
        fontSize: 19,
        fontWeight: 700,
        color: quote?.changePct > 0 ? C.green : quote?.changePct < 0 ? C.red : C.text,
        marginTop: 4,
        lineHeight: 1,
      }}>
        {quote?.price ? `$${fmt(quote.price)}` : "—"}
      </div>

      {quote?.volume != null && (
        <div style={{ fontSize: 11, color: C.textDim, marginTop: 3 }}>
          Vol {fmtK(quote.volume)}
        </div>
      )}
    </div>
  );
}

// ─── SECTOR HEATMAP ROW ───────────────────────────────────────────────────────
// Shows relative performance across all tracked symbols at a glance.
function SectorHeatmap({ quotes }) {
  const symbols = INDICES.filter((i) => quotes[i.symbol]?.changePct != null);
  if (symbols.length === 0) return null;

  const changes = symbols.map((i) => quotes[i.symbol].changePct);
  const max = Math.max(...changes.map(Math.abs), 0.01);

  return (
    <div style={{
      ...s.panel,
      padding: "8px 10px",
      marginBottom: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: C.amber, marginBottom: 6 }}>
        RELATIVE PERFORMANCE
      </div>
      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {symbols.map((idx) => {
          const chg = quotes[idx.symbol].changePct;
          const intensity = Math.min(1, Math.abs(chg) / max);
          const up = chg >= 0;
          const bg = up
            ? `rgba(34,197,94,${0.08 + intensity * 0.25})`
            : `rgba(239,68,68,${0.08 + intensity * 0.25})`;
          return (
            <div key={idx.symbol} style={{
              background: bg,
              border: `1px solid ${up ? C.green : C.red}33`,
              borderRadius: 2,
              padding: "4px 7px",
              textAlign: "center",
              minWidth: 52,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{idx.symbol}</div>
              <div style={{ fontSize: 11, color: up ? C.green : C.red, fontWeight: 700 }}>
                {pct(chg)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MARKETS TAB ──────────────────────────────────────────────────────────────
export function MarketsTab({ keys, onData }) {
  const [quotes,    setQuotes]    = useState({});
  const [chart,     setChart]     = useState([]);
  const [selected,  setSelected]  = useState("SPY");
  const [status,    setStatus]    = useState("offline");
  const [chartLoad, setChartLoad] = useState(false);
  const [lastUpdate,setLastUpdate]= useState(null);

  // ── Fetch snapshots (price + change + volume) ──
  const fetchQuotes = useCallback(async () => {
    if (!window.QUANT_SIGNAL_BACKEND) return;
    try {
      const syms = INDICES.map((i) => i.symbol).join(",");
      const res = await backendFetch(
        `${ALPACA_REST}/stocks/snapshots?symbols=${syms}`,
        `/api/snapshot?symbols=${syms}`
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();

      const q = {};
      Object.entries(data).forEach(([sym, snap]) => {
        const price    = snap.latestTrade?.p;
        const prevClose = snap.prevDailyBar?.c;
        q[sym] = {
          price,
          prevClose,
          changePct: price && prevClose
            ? ((price - prevClose) / prevClose) * 100
            : null,
          changeAbs: price && prevClose ? price - prevClose : null,
          volume:    snap.dailyBar?.v ?? null,
          vwap:      snap.dailyBar?.vw ?? null,
          high:      snap.dailyBar?.h ?? null,
          low:       snap.dailyBar?.l ?? null,
        };
      });

      setQuotes(q);
      setStatus("live");
      setLastUpdate(new Date().toLocaleTimeString());
      if (onData) onData("quotes", q);
    } catch {
      setStatus("offline");
    }
  }, [keys, onData]);

  // ── Fetch 30-day daily bars for chart ──
  const fetchChart = useCallback(async (sym) => {
    if (!window.QUANT_SIGNAL_BACKEND) return;
    setChartLoad(true);
    setChart([]);
    try {
      const end   = new Date().toISOString();
      const start = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const res = await backendFetch(
        `${ALPACA_REST}/stocks/${sym}/bars?timeframe=1Day&start=${start}&end=${end}&limit=30`,
        `/api/bars/${sym}?tf=1D`
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      if (data.bars) {
        setChart(data.bars.map((b) => ({
          date:  b.t.slice(5, 10),
          close: b.c,
          open:  b.o,
          high:  b.h,
          low:   b.l,
          vol:   b.v,
        })));
      }
    } catch {}
    setChartLoad(false);
  }, [keys]);

  // ── Poll quotes every 30s ──
  useEffect(() => {
    fetchQuotes();
    const t = setInterval(fetchQuotes, 30_000);
    return () => clearInterval(t);
  }, [fetchQuotes]);

  // ── Reload chart on symbol change ──
  useEffect(() => {
    fetchChart(selected);
    // Persist selected symbol
    localStorage.setItem("fterm_markets_sym", selected);
  }, [selected, fetchChart]);

  // ── Restore last selected symbol ──
  useEffect(() => {
    const saved = localStorage.getItem("fterm_markets_sym");
    if (saved && INDICES.find((i) => i.symbol === saved)) {
      setSelected(saved);
    }
  }, []);

  if (!keys.alpacaKey && !window.QUANT_SIGNAL_BACKEND) {
    return (
      <div style={{ ...s.panel, ...s.panelB, color: C.textDim, fontSize: 14 }}>
        <AlertCircle size={11} style={{ marginRight: 6, verticalAlign: "middle" }} />
        Data unavailable.
      </div>
    );
  }

  const selQuote = quotes[selected];
  const selIdx   = INDICES.find((i) => i.symbol === selected);

  // Build analyst context from current data
  const analystContext =
    `Market snapshot — ${new Date().toISOString().split("T")[0]}:\n` +
    INDICES
      .filter((i) => quotes[i.symbol]?.changePct != null)
      .map((i) => {
        const q = quotes[i.symbol];
        return `${i.symbol} (${i.name}): $${fmt(q.price)}, ${pct(q.changePct)}, vol ${fmtK(q.volume)}`;
      })
      .join("\n") +
    `\n\nSelected: ${selected} — 30-day price chart loaded (${chart.length} bars).\n` +
    (selQuote ? `Current: $${fmt(selQuote.price)}, day range $${fmt(selQuote.low)}–$${fmt(selQuote.high)}, VWAP $${fmt(selQuote.vwap)}` : "") +
    `\n\nAnalyze the relative performance across these instruments. What does the cross-asset picture suggest about current market regime and sector rotation? Note any divergences (e.g. equities up / TLT down / GLD bid / HYG weak). Then analyze the 30-day ${selected} price action specifically.`;

  return (
    <div>
      {/* Quote grid */}
      {/* ⚠⚠ auto-fit + minmax, NOT four fixed tracks. `1fr` is minmax(AUTO, 1fr),
          so the tracks carry a min-content floor and could not compress.
          ⚠⚠ THE TOP ROW BINDS, NOT THE PRICE — the same thing that catches every
          one of these panels. QuoteCard's first row is a flex with
          justifyContent: space-between, so the name block and the change badge
          sit SIDE BY SIDE and their floors add:
            name   "Financials" (fontSize 11, wraps to its longest word;
                   XLF's "Financials ETF" is the longest of the eight)   ~66px
          + badge  "+1.24%" 6ch @ fontSize 13 + 10px padding + 2px border ~59px
                                                                        = ~125px
            price  "$612.34" 7ch @ fontSize 19 bold                       ~80px
          + card padding "9px 11px" (22) and borders (~3)                  ~25px
          Cell floor = 125 + 25 = ~150px, so four tracks wanted ~624px against
          the 387px this grid gets on a folded phone.
          ⚠⚠ THIS CORRECTS PHASE 2C, WHICH LEFT THIS GRID ALONE ON A MEASUREMENT
          OF ~70px. That number counted the symbol and the padding and missed that
          the name and the badge share a row — the same error made on Bonds' top
          stats row. 4 x 70 + 24 = 304px looked like it fitted inside 387px. It
          never did.
          ⚠ 168 gives 18px over the floor. 3-across is not reachable here: it
          would need tracks under ~124px, below the 150px the card itself
          occupies, so 2-across on a phone is forced rather than chosen.
          ⚠ Wide screens now get MORE than four columns (5 at ~1024px, 8 at
          ~1440px). That is an improvement, not drift: four fixed tracks stretched
          each card past 600px on a wide monitor. With eight items some widths
          land ragged (5+3, 7+1) — unavoidable with equal tracks, and the phone
          is what this pass is for.
          ⚠⚠ UNMEASURED — 0.6em advance arithmetic, not a render. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
        gap: 8,
        marginBottom: 10,
      }}>
        {GRID_SYMBOLS.map((idx) => (
          <QuoteCard
            key={idx.symbol}
            idx={idx}
            quote={quotes[idx.symbol]}
            selected={selected}
            onClick={() => setSelected(idx.symbol)}
          />
        ))}
      </div>

      {/* Sector heatmap — all 12 symbols */}
      <SectorHeatmap quotes={quotes} />

      {/* Main chart panel */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={s.panelT}>{selected} — 30-DAY DAILY</span>
            {selQuote?.changePct != null && (
              <span style={s.badge(selQuote.changePct >= 0)}>
                {pct(selQuote.changePct)} today
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <LiveTicker
              symbol={selected}
              alpacaKey={keys.alpacaKey}
              alpacaSecret={keys.alpacaSecret}
            />
            <DataStatus
              status={status}
              source=""
              timestamp={lastUpdate}
            />
            <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={fetchQuotes}>
              <RefreshCw size={10} />
            </button>
          </div>
        </div>

        <div style={s.panelB}>
          {/* Price stats row */}
          {selQuote && (
            <div style={{
              display: "grid",
              // ⚠⚠ auto-fit + minmax, NOT repeat(6, 1fr) — `1fr` is
              // minmax(AUTO, 1fr) and that `auto` floor is min-content, so six
              // fixed tracks could not compress and overflowed a 365px panel on a
              // folded phone. Same trap as MacroTab's YieldGrid, DIFFERENT number:
              // this grid's cells are wider, so 72 would clip the price.
              // ⚠ DERIVED FROM THIS GRID'S OWN CONTENT, not copied. A cell is two
              // stacked divs with NO padding, border or background, so its floor is
              // whichever of the two lines is wider:
              //   label  s.lbl — fontSize 11 + 0.1em letter-spacing, and NO nowrap,
              //          so a two-word label wraps and its min-content is the
              //          longest WORD: "CHANGE", 6 x (11*0.6 + 1.1) = ~46px
              //   value  fontSize 17 bold, no spaces, so the whole string:
              //          "$600.12" (7ch x 10.2) = ~71px for this ETF universe,
              //          "$1284.02" (8ch) = ~82px for a high-priced name reached
              //          through search
              // The value governs. At 6 x 71 + 5 gaps x 8 = 472px the grid was
              // already ~107px past the panel; an 8-character price made it 532.
              // 88 holds that 8-character case with headroom.
              // ⚠ 88 also breaks the rows where the content pairs: 3-across on a
              // phone puts PRICE / CHANGE / PREV CLOSE on one line and DAY HIGH /
              // DAY LOW / VWAP on the next, so the day's range stays together
              // rather than splitting across rows as a 4-across break would.
              // Returns to 6-across from a ~640px viewport, so desktop is
              // unchanged, and degrades to 2-across at 320px without overflowing.
              // ⚠⚠ UNMEASURED. Every width above comes from the monospace advance
              // (0.6em), not a real render. This is the number to adjust on a
              // device — raise it if a price clips, lower it if a phone shows
              // 2-across where 3 would fit.
              gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))",
              gap: 8,
              marginBottom: 12,
              padding: "8px 0",
              borderBottom: `1px solid ${C.border}`,
            }}>
              {[
                ["PRICE",     `$${fmt(selQuote.price)}`],
                ["CHANGE",    `${selQuote.changeAbs != null ? (selQuote.changeAbs > 0 ? "+" : "") + fmt(selQuote.changeAbs) : "—"}`],
                ["PREV CLOSE",`$${fmt(selQuote.prevClose)}`],
                ["DAY HIGH",  `$${fmt(selQuote.high)}`],
                ["DAY LOW",   `$${fmt(selQuote.low)}`],
                ["VWAP",      `$${fmt(selQuote.vwap)}`],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={s.lbl}>{label}</div>
                  <div style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: label === "CHANGE"
                      ? clr(selQuote.changeAbs)
                      : C.text,
                  }}>
                    {val}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Price chart */}
          {chartLoad ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              LOADING CHART...
            </div>
          ) : chart.length > 0 ? (
            <>
              {/* Area price chart */}
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chart} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.amber} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.amber} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: C.textDim }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: C.textDim }}
                    tickLine={false}
                    axisLine={false}
                    domain={["auto", "auto"]}
                    tickFormatter={(v) => `$${v}`}
                    width={50}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="close"
                    stroke={C.amber}
                    strokeWidth={1.5}
                    fill="url(#priceGrad)"
                    dot={false}
                    activeDot={{ r: 3, fill: C.amber }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Volume bar chart */}
              <ResponsiveContainer width="100%" height={50}>
                <BarChart data={chart} margin={{ top: 2, right: 4, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis hide domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font }}
                    formatter={(v) => [fmtK(v), "Volume"]}
                    labelFormatter={() => ""}
                  />
                  <Bar
                    dataKey="vol"
                    fill={C.teal}
                    opacity={0.5}
                    radius={[1, 1, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>

              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                Volume (bottom panel) · 30 trading days
              </div>
            </>
          ) : (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              No chart data available
            </div>
          )}

          {/* Analyst button */}
          {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
            <div style={{ marginTop: 12 }}>
              <AnalystButton
                panel="signal_board"
                label="SYNTHESIZE"
                context={analystContext}
              />
            </div>
          )}

          {/* Ticker news */}
          <TickerNewsPanel ticker={selected} keys={keys} />
        </div>
      </div>

      {/* Extended symbol panel — non-grid symbols */}
      {Object.keys(quotes).length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>EXTENDED COVERAGE</span>
            <span style={{ ...s.tag(), fontSize: 11 }}>Click to chart</span>
          </div>
          <div style={{ padding: "6px 10px" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {INDICES.slice(8).map((idx) => {
                const q   = quotes[idx.symbol];
                const up  = q?.changePct >= 0;
                const sel = selected === idx.symbol;
                return (
                  <button
                    key={idx.symbol}
                    onClick={() => setSelected(idx.symbol)}
                    style={{
                      ...s.btn(sel ? "amber" : "default"),
                      padding: "5px 10px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{idx.symbol}</span>
                    {q?.price && (
                      <span style={{ fontSize: 11, color: up ? C.green : C.red }}>
                        ${fmt(q.price)} {pct(q.changePct)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
        Quotes refreshed every 30s · Live bid/ask ·{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}
